import { SITE_LEVEL_PAGE_ID } from '@wix/a11y-wizard-common';
import { Box } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials/react';
import { CollapseList, StatusIcon, TaskListItem, ToggleType } from '@wix/rules-panel-ui';
import React, { useEffect, useMemo, useState } from 'react';
import { AnalysisState, usePanelStateAtom } from '../../../../../hooks/rules-state';
import { useA11yWizardAdapter } from '../../../../../hooks/use-a11y-wizard-adapter';
import { useGetRuleIdToLabelMap } from '../../../../../hooks/use-get-ruleId-to-label-map';
import { useQueryRuleExecutionResultsQuery } from '../../../../../queries/use-query-rule-execution-results-query';
import { resolveGroupAnalysisState } from '../../../../../utils/resolve-group-analysis-state';
import { useSiteLevelRuleResults } from '../../rule-groups/site-level-group/use-site-level-rule-results';
import { useGetPageRuleIds } from '../utils/use-get-page-rule-ids';
import { useShowFetchErrorModal } from '../utils/use-show-fetch-error-modal';
import { CollapseActionsProvider, ItemScopeProvider, useCloseSelf } from './context/collapse-actions-context';
import { useOpenNextItem } from './hooks/use-open-next-item';
import { RuleContentWithCollapse } from './rule-content-with-collapse';
import { useHandleUnsavedChangesOnToggle } from './utils/use-handle-unsaved-changes-on-toggle';
export const SiteLevelTaskList = _ref => {
  let {
    isCardOpen
  } = _ref;
  const showFetchErrorModal = useShowFetchErrorModal();
  const {
    getGroupData,
    setGroupData
  } = usePanelStateAtom();
  const {
    refetch: refetchRuleExecutionResults
  } = useQueryRuleExecutionResultsQuery(SITE_LEVEL_PAGE_ID, () => showFetchErrorModal({
    cancelCallback: () => {
      setGroupData(SITE_LEVEL_PAGE_ID, {
        analysisState: AnalysisState.AnalyzeDonePendingResults
      });
      closeCard();
    },
    tryAgainCallback: refetchRuleExecutionResults
  }));
  const closeCard = useCloseSelf();
  const groupData = getGroupData(SITE_LEVEL_PAGE_ID);
  if (!groupData) {
    throw new Error('Group data not found for site level');
  }
  const {
    analysisState
  } = groupData;
  const {
    isFetchResultSucceeded
  } = resolveGroupAnalysisState(analysisState);
  const ruleResults = useSiteLevelRuleResults();
  const [openItems, setOpenItems] = useState(new Set());

  // Auto-fetch when group is open and results become pending
  useEffect(() => {
    if (isCardOpen && analysisState === AnalysisState.AnalyzeDonePendingResults) {
      refetchRuleExecutionResults();
    }
  }, [analysisState, isCardOpen, refetchRuleExecutionResults]);
  const {
    t
  } = useTranslation();
  const {
    siteLevelruleIdToLabelKeyMap
  } = useGetRuleIdToLabelMap();
  const {
    capabilities
  } = useA11yWizardAdapter();
  const compactPanelChrome = capabilities.compactPanelChrome;
  const ruleIdsForSkeleton = useGetPageRuleIds(SITE_LEVEL_PAGE_ID);
  const handleSetOpenItems = useHandleUnsavedChangesOnToggle(openItems, setOpenItems);
  const ruleResultsForAutoExpand = useMemo(() => {
    return ruleResults.map(result => {
      return {
        id: result.ruleId,
        status: result.status
      };
    });
  }, [ruleResults]);
  const {
    handlePulseComplete
  } = useOpenNextItem({
    items: ruleResultsForAutoExpand,
    pageId: SITE_LEVEL_PAGE_ID,
    setOpenItems
  });
  const tasks = useMemo(() => {
    if (!isFetchResultSucceeded) {
      return ruleIdsForSkeleton == null ? void 0 : ruleIdsForSkeleton.map(ruleId => {
        return /*#__PURE__*/React.createElement(ItemScopeProvider, {
          id: ruleId,
          key: ruleId
        }, /*#__PURE__*/React.createElement(TaskListItem, {
          id: ruleId,
          isLoading: true,
          showBottomShadow: !compactPanelChrome,
          showTopShadow: !compactPanelChrome,
          title: t(siteLevelruleIdToLabelKeyMap[ruleId]),
          variant: "collapsible"
        }));
      });
    }
    return ruleResults.map((ruleResult, index, array) => {
      const isLast = index === array.length - 1;
      return /*#__PURE__*/React.createElement(ItemScopeProvider, {
        id: ruleResult.ruleId,
        key: ruleResult.ruleId
      }, /*#__PURE__*/React.createElement(TaskListItem, {
        id: ruleResult.ruleId,
        prefix: /*#__PURE__*/React.createElement(StatusIcon, {
          status: ruleResult.status,
          onComplete: handlePulseComplete
        }),
        showBottomShadow: !isLast && !compactPanelChrome,
        showTopShadow: !compactPanelChrome,
        title: ruleResult.label,
        variant: "collapsible",
        onToggle: (_id, isOpen) => {
          ruleResult.onRuleToggle == null || ruleResult.onRuleToggle(isOpen ? ToggleType.Expand : ToggleType.Collapse, index, true // initiatedByUser
          );
        }
      }, /*#__PURE__*/React.createElement(Box, {
        dataHook: "site-task-list-item-content",
        padding: "SP3 SP6 SP6 SP6"
      }, /*#__PURE__*/React.createElement(RuleContentWithCollapse, {
        ruleResult: ruleResult
      }))));
    });
  }, [isFetchResultSucceeded, ruleResults, ruleIdsForSkeleton, compactPanelChrome, t, siteLevelruleIdToLabelKeyMap, handlePulseComplete]);
  return /*#__PURE__*/React.createElement(CollapseActionsProvider, {
    setOpenItems: handleSetOpenItems
  }, /*#__PURE__*/React.createElement(CollapseList, {
    autoScroll: true,
    contentGap: 0,
    openItems: openItems,
    setOpenItems: handleSetOpenItems
  }, tasks));
};
//# sourceMappingURL=site-level-task-list.js.map